import { Heading, Tooltip } from '@mm/components';
import CompanyModel from '@mm/state/model/CompanyModel';
import { StoreState } from '@mm/state/store-interfaces';

import { Formik, FormikProps } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import FormFieldWrapper from '../../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { PhoneInput } from '../../../../../../../common/components/PhoneInput/PhoneInput';
import { WrappedTextInput } from '../../../../../../../common/components/WrappedTextInput/WrappedTextInput';
import { getDeviceInfo } from '../../../../../../../common/hooks/use-device-info';
import { useResolutionInfo } from '@mm/hooks';
import CompleteRegisterButton from '../../../2-UserInfo/CompleteRegisterButton/CompleteRegisterButton';
import {
  fields,
  getValidationSchema,
  UserInfoFormValues,
} from '../../../2-UserInfo/user-info-form-helpers';
import './WelcomePage.scss';
import {
  isFacebookUserSelector,
  selectCurrentUser,
} from '@mm/state/selectors/userSelector';
import { OnboardingLogosPanel } from '../../../../../XRayOnboarding/OnboardingLogosPanel/OnboardingLogosPanel';
import PrimarySelect from '../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { dealSizeOptions, jobRoleOptions } from '../../../2-UserInfo/constants';
import classNames from 'classnames';

type Props = {
  onStartPress: (
    companyName: string,
    businessEmail: string,
    companyWebsite: string,
    phoneNumber: string,
    jobRole: string,
    clientsCharge: string,
    goal?: string,
  ) => void;
  company: CompanyModel;
};

export const WelcomePage = (props: Props) => {
  const { onStartPress, company } = props;
  const { isMobile } = useResolutionInfo();
  const { isAndroid, isIOS } = getDeviceInfo();
  const userCountry = useSelector((store: StoreState) => store.env.userCountry);
  const user = useSelector(selectCurrentUser);
  const isFBUser = useSelector(isFacebookUserSelector);

  const isBusinessEmail = () => {
    const stopList = [
      'gmail.com',
      'yahoo.com',
      'hotmail.com',
      'aol.com',
      'outlook.com',
      'icloud.com',
      'mac.com',
      'msn.com',
      'sbcglobal.net',
      'yahoo.co.uk',
    ];
    const res = stopList.filter(v => user?.email.includes(v));
    return !res.length;
  };

  const validationSchema = getValidationSchema();
  const initialValues = {
    businessEmail: isBusinessEmail()
      ? user?.email
      : company.businessEmail ?? '',
    companyName: '',
    companyWebsite: company.website ?? '',
    phoneNumber: company.phoneNumber ?? '',
    goal: '',
    jobRole: '',
    clientsCharge: '',
  };

  const onSignupClick = async (formValues: UserInfoFormValues) => {
    const {
      businessEmail,
      companyName,
      companyWebsite,
      phoneNumber,
      jobRole,
      clientsCharge,
      goal,
    } = formValues;

    onStartPress(
      companyName,
      businessEmail,
      companyWebsite,
      phoneNumber,
      jobRole,
      clientsCharge,
      goal,
    );
  };

  const getIsValid = (formProps: FormikProps<UserInfoFormValues>) => {
    if (isMobile) {
      return false;
    }
    return !formProps.isValid;
  };

  return (
    <div className="welcome_page_wrapper">
      {!isMobile && (
        <OnboardingLogosPanel className="welcome-page__left-section" />
      )}

      <div className="welcome-page__content">
        <div className="welcome-page__headers">
          <Heading
            className="welcome-page__content-title"
            size={1}
            text="Welcome to MobileMonkey"
          />
          <Heading
            className="welcome-page__content-subtitle"
            size={4}
            text="Please tell us more about your business"
          />
        </div>
        <div className="welcome-page__form-container">
          <Formik
            onSubmit={onSignupClick}
            initialValues={initialValues}
            validationSchema={validationSchema}
            // Until the below issue is fixed on mobile only validate on submit
            // due to autocomplete issues with certain combos of Android/Chrome
            // https://github.com/facebook/react/issues/1159
            validateOnChange={!isMobile}
            validateOnBlur={!isMobile}
            enableReinitialize
          >
            {(formProps: FormikProps<UserInfoFormValues>) => (
              <form onSubmit={() => onSignupClick(formProps.values)}>
                <div className="welcome-page__form-wrapper">
                  <WrappedTextInput
                    label="Company Name:"
                    inputClassName="welcome-page__form-input"
                    formProps={formProps}
                    fieldName={fields.companyName}
                  />

                  {!isBusinessEmail() && (
                    <WrappedTextInput
                      label="Business Email:"
                      inputClassName="welcome-page__form-input"
                      formProps={formProps}
                      fieldName={fields.businessEmail}
                    />
                  )}
                  <Tooltip
                    position="right"
                    color="light"
                    useHover
                    noDelayClose
                    anchorElement={
                      <WrappedTextInput
                        label="Website URL:"
                        inputClassName="welcome-page__form-input"
                        formProps={formProps}
                        fieldName={fields.companyWebsite}
                      />
                    }
                  >
                    Please, list your website’s URL that you would like to
                    collect its visitor information and generate traffic to
                  </Tooltip>

                  {isFBUser && (
                    <FormFieldWrapper
                      className="welcome-page_form-field-wrapper"
                      label="Phone Number"
                      error={formProps.errors.phoneNumber}
                      touched={formProps.touched.phoneNumber}
                    >
                      <PhoneInput
                        country={userCountry}
                        name={fields.phoneNumber}
                        className="user-info__phone-input"
                        value={formProps.values.phoneNumber}
                        onChange={value =>
                          formProps.setFieldValue('phoneNumber', value)
                        }
                        onBlur={() => formProps.setFieldTouched('phoneNumber')}
                        errorState={
                          formProps.touched.phoneNumber &&
                          !!formProps.errors.phoneNumber
                        }
                      />
                    </FormFieldWrapper>
                  )}

                  <FormFieldWrapper
                    className="welcome-page_form-field-wrapper"
                    label="What is your average deal size?"
                    error={formProps.errors.clientsCharge}
                    touched={formProps.touched.clientsCharge}
                  >
                    <div
                      onMouseDown={() =>
                        formProps.setFieldTouched('clientsCharge')
                      }
                    >
                      <PrimarySelect
                        className={classNames(
                          'welcome-page__primary-select-dropdown',
                          {
                            'welcome-page__primary-select-dropdown-invalid':
                              !isMobile &&
                              formProps.touched.clientsCharge &&
                              formProps.errors.clientsCharge,
                          },
                        )}
                        placeholder="Choose"
                        options={dealSizeOptions}
                        value={dealSizeOptions.find(
                          option =>
                            option.name === formProps.values.clientsCharge,
                        )}
                        onChange={value =>
                          formProps.setFieldValue('clientsCharge', value.name)
                        }
                      />
                    </div>
                  </FormFieldWrapper>

                  <FormFieldWrapper
                    className="welcome-page_form-field-wrapper"
                    label="What’s your job role?"
                    error={formProps.errors.jobRole}
                    touched={formProps.touched.jobRole}
                  >
                    <div
                      onMouseDown={() => formProps.setFieldTouched('jobRole')}
                    >
                      <PrimarySelect
                        className={classNames(
                          'welcome-page__primary-select-dropdown',
                          {
                            'welcome-page__primary-select-dropdown-invalid':
                              !isMobile &&
                              formProps.touched.jobRole &&
                              formProps.errors.jobRole,
                          },
                        )}
                        placeholder="Choose"
                        options={jobRoleOptions}
                        value={jobRoleOptions.find(
                          option => option.name === formProps.values.jobRole,
                        )}
                        onChange={value =>
                          formProps.setFieldValue('jobRole', value.name)
                        }
                      />
                    </div>
                  </FormFieldWrapper>

                  <FormFieldWrapper
                    className="welcome-page_form-field-wrapper"
                    label="What is your goal with MobileMonkey?"
                    labelNote="(optional)"
                  >
                    <textarea
                      className="welcome-page_textarea"
                      name={fields.goal}
                      onChange={formProps.handleChange}
                      onBlur={formProps.handleBlur}
                      value={formProps.values.goal}
                    />
                  </FormFieldWrapper>

                  <input
                    className="user-info__submit-input"
                    type="submit"
                    disabled={getIsValid(formProps)}
                  />
                  <CompleteRegisterButton
                    useNewDesign
                    className={classNames(
                      'welcome-page__complete-register-button',
                      {
                        'welcome-page__complete-register-button--disabled':
                          getIsValid(formProps),
                      },
                    )}
                    disabled={getIsValid(formProps)}
                    isMobileApp={isAndroid || isIOS}
                    onSentToMessenger={formProps.handleSubmit}
                  />
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
